/**
 * Map stored AcroForm field metadata into editor annotation objects.
 * mode: "forMe" (esign-setup self fields) | "forOthers" (public form respondent fields)
 */

const TYPE_MAP_FOR_ME = {
  text: "my_text",
  date: "date",
  checkbox: "checkmark",
  dropdown: "dropdown",
  signature: "my_signature",
};

const TYPE_MAP_FOR_OTHERS = {
  text: "signer_text",
  date: "signer_date",
  checkbox: "signer_checkmark",
  dropdown: "signer_dropdown",
  signature: "signer_initials",
};

/**
 * @param {Array<object>} fields
 * @param {{ mode?: "forMe"|"forOthers", signerId?: number|null, signerColor?: string|null }} options
 * @returns {Array<object>}
 */
export function acroformFieldsToAnnotations(fields, options = {}) {
  if (!Array.isArray(fields) || !fields.length) return [];

  const mode = options.mode === "forOthers" ? "forOthers" : "forMe";
  const TYPE_MAP = mode === "forOthers" ? TYPE_MAP_FOR_OTHERS : TYPE_MAP_FOR_ME;
  const signerId = options.signerId != null ? options.signerId : mode === "forOthers" ? 1 : null;
  const signerColor =
    options.signerColor ||
    (mode === "forOthers" ? "rgb(255 226 139 / 98%)" : null);

  const baseId = Date.now();
  return fields
    .map((field, index) => {
      const type = TYPE_MAP[field.pdfType] || null;
      if (!type) return null;

      const page_no = field.page_no || 1;
      const x = Number(field.x);
      const y = Number(field.y);
      const width = Number(field.width);
      const height = Number(field.height);
      if ([x, y, width, height].some((n) => Number.isNaN(n))) return null;

      const isTextLike =
        type === "my_text" ||
        type === "signer_text" ||
        type === "date" ||
        type === "signer_date";

      const annotation = {
        id: baseId + index,
        type,
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
        width: Math.max(0.1, Math.min(100, width)),
        height: Math.max(0.1, Math.min(100, height)),
        page_no,
        text: "",
        checked: false,
        imageUrl: null,
        format: [],
        fontSize: isTextLike ? 14 : undefined,
        fontFamily: "Helvetica",
        lineHeight: 1,
        dateFormat:
          type === "date" || type === "signer_date" ? "MMM dd, yyyy" : undefined,
        isPendingText: false,
        backgroundColor:
          mode === "forOthers"
            ? signerColor
            : type === "my_text" || type === "date"
              ? "rgba(0, 247, 255, 0.25)"
              : type === "checkmark"
                ? "rgba(0, 200, 100, 0.25)"
                : "rgba(255, 200, 0, 0.25)",
        borderColor:
          mode === "forOthers" ? signerColor : "rgba(0, 0, 0, 0.23)",
        signerId,
        signerColor: mode === "forOthers" ? signerColor : null,
        isRequired: false,
        fieldLabel: field.name || "",
        source: "acroform",
        acroform_name: field.name || "",
        pageWidth: field.pageWidth || null,
        preserveBox: true,
      };

      if (
        (type === "dropdown" || type === "signer_dropdown") &&
        Array.isArray(field.options)
      ) {
        annotation.options = field.options;
        annotation.format = field.options;
        annotation.text = "";
      }

      return annotation;
    })
    .filter(Boolean);
}

/**
 * Group flat annotations into { [pageNum]: Annotation[] } shape used by the editor.
 */
export function groupAnnotationsByPage(annotations) {
  return (annotations || []).reduce((acc, annotation) => {
    const pageNum = annotation.page_no || 1;
    if (!acc[pageNum]) acc[pageNum] = [];
    acc[pageNum].push(annotation);
    return acc;
  }, {});
}
